import React, { useState } from 'react';
import { AlertTriangle, GitMerge } from 'lucide-react';
import { AdminApi } from '../../services/api';
import { Button, Modal } from '../../shared/ui';
import { estConflitClientsDifferents } from './transfertFusion';

// Mobile et tablette : cibles tactiles d'au moins 40 px, boutons de validation
// empilés pleine largeur sur téléphone. À partir de sm / lg, rendu d'origine.
const TACTILE = 'min-h-[40px] lg:min-h-0';
// Sur téléphone, les boutons restent collés en bas de la fenêtre qui défile.
const ACTIONS_FENETRE = 'flex flex-col gap-2 sm:flex-row sm:justify-end max-sm:sticky max-sm:-bottom-5 max-sm:z-10 max-sm:bg-white max-sm:-mx-5 max-sm:px-5 max-sm:pb-5 max-sm:pt-3 max-sm:-mb-5';
const BOUTON_FENETRE = `w-full sm:w-auto ${TACTILE}`;

type ResultatFusion = Awaited<ReturnType<typeof AdminApi.fusionner>>;

/**
 * Fusion du dossier ouvert dans un autre (même panne envoyée deux fois).
 * Si les dossiers appartiennent à des clients différents, le serveur refuse
 * (409) : son message est affiché et « Fusionner quand même » renvoie la
 * demande avec la confirmation.
 */
export const FusionDossierModal: React.FC<{
  ticketId: string;
  onClose: () => void;
  onFusionne: (resultat: ResultatFusion) => void;
}> = ({ ticketId, onClose, onFusionne }) => {
  const [reference, setReference] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [conflit, setConflit] = useState<string | null>(null);

  const fusionner = async (confirmerClientsDifferents: boolean) => {
    const ref = reference.trim();
    if (!ref) return;
    setEnCours(true);
    setErreur(null);
    try {
      const r = await AdminApi.fusionner(ticketId, confirmerClientsDifferents
        ? { cibleRef: ref, confirmerClientsDifferents: true }
        : { cibleRef: ref });
      onFusionne(r);
    } catch (e) {
      if (!confirmerClientsDifferents && estConflitClientsDifferents(e)) {
        setConflit(e.message || 'Les deux dossiers appartiennent à des clients différents.');
      } else {
        setErreur(e instanceof Error ? e.message : 'Fusion impossible');
      }
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Fusionner ce dossier" size="sm">
      <form onSubmit={e => { e.preventDefault(); fusionner(false); }} className="space-y-4">
        <p className="text-sm text-graphite">Ce dossier sera clos et son contenu (messages, pièces, e-mails, appels) déplacé dans le dossier indiqué.</p>
        <label className="block">
          <span className="text-xs font-semibold text-graphite">Référence du dossier qui garde tout</span>
          <input
            value={reference}
            onChange={e => { setReference(e.target.value); setConflit(null); setErreur(null); }}
            placeholder="Ex. : 26-0012"
            autoFocus
            className="mt-1 block w-full rounded-lg border border-trait bg-white py-2 px-3 text-base sm:text-sm text-encre tnum focus:border-sapin-600 focus:ring-2 focus:ring-sapin-600/20 focus:outline-none"
          />
        </label>

        {conflit && (
          <div role="alert" className="rounded-lg border border-[#F0D9AE] bg-[#FBF1E1] p-3 text-sm text-[#7A4A00]">
            <p className="flex items-start gap-2"><AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />{conflit}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button variant="primary" size="sm" onClick={() => fusionner(true)} disabled={enCours} className={`grow sm:grow-0 ${TACTILE}`}>Fusionner quand même</Button>
              <Button variant="secondary" size="sm" onClick={() => setConflit(null)} disabled={enCours} className={`grow sm:grow-0 ${TACTILE}`}>Annuler</Button>
            </div>
          </div>
        )}
        {erreur && <p role="alert" className="text-sm text-red-700">{erreur}</p>}

        {!conflit && (
          <div data-testid="actions-fenetre" className={ACTIONS_FENETRE}>
            <Button variant="secondary" onClick={onClose} disabled={enCours} className={BOUTON_FENETRE}>Annuler</Button>
            <Button type="submit" variant="primary" disabled={enCours || !reference.trim()} className={BOUTON_FENETRE}>
              <GitMerge className="w-4 h-4 mr-1" />{enCours ? 'Fusion…' : 'Fusionner'}
            </Button>
          </div>
        )}
      </form>
    </Modal>
  );
};

export default FusionDossierModal;
